// Every venue and organisation the member can open, on the full page (`/venues`, spec/13 §1–§2): a card per
// organisation — its name as the card's heading, how many of its venues are the member's, and "Open organisation"
// where the switcher would offer it (`opensOrganization`) — and each venue a row that opens its Floor. Venues whose
// organisation is not among the member's are listed last, in a card of their own.

import { useTranslation } from '@clavian/i18n/react';
import { initialsOf, VenuePicker, type VenuePickerVenue } from '@clavian/ui-web';
import { Link } from '@tanstack/react-router';
import { useId, type ReactNode } from 'react';

import type { MyOrganization, MyVenue } from '../api/me';
import { Icon } from './nav-icons';
import { usePlaceSwitch } from './place-switch';
import { useVenueLive } from './venue-live';
import { opensOrganization } from './VenueSwitcher';

export function VenueDirectory({
  organizations,
  venues,
}: {
  readonly organizations: readonly MyOrganization[];
  readonly venues: readonly MyVenue[];
}): ReactNode {
  const listed = organizations.filter((organization) => venues.some((venue) => venue.organizationId === organization.id));
  const unnamed = venues.filter((venue) => !organizations.some((organization) => organization.id === venue.organizationId));
  return (
    <div className="console-venue-cards">
      {listed.map((organization) => (
        <OrganizationCard
          key={organization.id}
          organization={organization}
          venues={venues.filter((venue) => venue.organizationId === organization.id)}
        />
      ))}
      {unnamed.length > 0 ? (
        <section className="console-section console-section--rows">
          <VenueList venues={unnamed} />
        </section>
      ) : null}
    </div>
  );
}

function OrganizationCard({ organization, venues }: { readonly organization: MyOrganization; readonly venues: readonly MyVenue[] }): ReactNode {
  const { t } = useTranslation('console');
  const headingId = useId();
  return (
    <section className="console-section console-section--rows" aria-labelledby={headingId}>
      <header className="console-org-card__head">
        <span className="console-org-card__mark" aria-hidden="true">
          {initialsOf(organization.name)}
        </span>
        <div className="console-org-card__heading">
          <h2 id={headingId} className="console-org-card__name">
            {organization.name}
          </h2>
          <p className="console-org-card__count">{t('chooseVenue.venueCount', { count: venues.length })}</p>
        </div>
        {opensOrganization(organization) ? (
          <Link to="/o/$orgSlug" params={{ orgSlug: organization.slug }} className="console-org-card__open">
            {t('chooseVenue.openOrganisation')}
            <Icon name="arrowRight" />
          </Link>
        ) : null}
      </header>
      <VenueList venues={venues} />
    </section>
  );
}

function VenueList({ venues }: { readonly venues: readonly MyVenue[] }): ReactNode {
  const { t } = useTranslation('console');
  const live = useVenueLive(venues);
  const place = usePlaceSwitch();
  const cards: VenuePickerVenue[] = venues.map((venue) => ({
    id: venue.id,
    name: venue.name,
    status: venue.status === 'live' ? 'open' : venue.status,
    live: live.get(venue.id),
  }));
  return (
    <VenuePicker
      label={t('chooseVenue.venuesLabel')}
      venues={cards}
      render={(card, parts) => (
        <Link
          to="/v/$venueSlug"
          params={{ venueSlug: venues.find((venue) => venue.id === card.id)?.slug ?? '' }}
          {...parts}
          onClick={() => place.begin(card.name)}
        />
      )}
    />
  );
}
